/**
 * ClientPortalLocationsBySizeChart Hook
 *
 * Renders a donut chart showing the distribution of locations across size
 * buckets, plus a legend on the right with colored dots, label, and the
 * formatted "<percent>% (<count>)" value. Bucket labels and the chart title
 * are pre-localized server-side; this hook only handles tooltip strings via
 * the connected-render data-i18n map (ENA-9328), falling back to window.I18n.
 *
 * Segment colors (in bucket order: xs / s / m / l): blue / orange / gray /
 * green.
 */

import { patchTooltipDelay } from "../utils/highcharts_tooltip_delay"
import { applyHighchartsTheme } from "../utils/highcharts_theme"

const SEGMENT_COLORS = ["#0086E6", "#F97316", "#9CA3AF", "#10B981"]

export const ClientPortalLocationsBySizeChart = {
  mounted() {
    const chartData = JSON.parse(this.el.dataset.chartData || "[]")
    this.i18n = JSON.parse(this.el.dataset.i18n || "null") || window.I18n || {}
    this.renderChart(chartData)

    this.handleEvent("update-locations-by-size-chart", ({ data }) => {
      this.renderChart(data)
    })
  },

  destroyed() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  },

  renderChart(chartData) {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }

    // Capture for use inside Highcharts callbacks where `this` is rebound.
    const escapeHtml = (str) => this.escapeHtml(str)
    const t = (key) => this.i18n[key] || key

    const totalCount = chartData.reduce(
      (sum, d) => sum + (d.location_count || 0),
      0,
    )

    const segments = chartData.map((d, i) => {
      const color = SEGMENT_COLORS[i % SEGMENT_COLORS.length]
      const pct = totalCount > 0 ? (d.location_count / totalCount) * 100 : 0
      return { ...d, color, pct }
    })

    this.el.innerHTML = `
      <div class="flex w-full items-center gap-[24px]">
        <div class="locations-by-size-canvas shrink-0" style="width: 120px /* root-scale: legacy-px */; height: 120px /* root-scale: legacy-px */;"></div>
        <div class="locations-by-size-legend flex-1 flex flex-col gap-[12px]"></div>
      </div>`

    const canvasEl = this.el.querySelector(".locations-by-size-canvas")
    const legendEl = this.el.querySelector(".locations-by-size-legend")

    legendEl.innerHTML = segments
      .map(
        (s) => `
      <div class="flex items-center gap-[8px] text-[length:0.8125rem]">
        <span class="inline-block w-[8px] h-[8px] rounded-full shrink-0" style="background-color: ${s.color}"></span>
        <span class="text-gray-700 truncate">${this.escapeHtml(s.bucket)}</span>
        <span class="ml-auto font-semibold text-black tabular-nums">${this.formatPercent(s.pct)} <span class="text-gray-500 font-normal">(${(s.location_count || 0).toLocaleString()})</span></span>
      </div>`,
      )
      .join("")

    if (totalCount === 0) {
      // Render an empty placeholder ring so the layout doesn't collapse.
      canvasEl.innerHTML = `
        <div class="w-full h-full rounded-full" style="background: conic-gradient(#F3F4F6 0 100%); mask: radial-gradient(circle, transparent 55%, #000 56%); -webkit-mask: radial-gradient(circle, transparent 55%, #000 56%);"></div>`
      return
    }

    import("highcharts").then((Highcharts) => {
      patchTooltipDelay(Highcharts.default)
      applyHighchartsTheme(Highcharts.default)

      this.chart = Highcharts.default.chart(canvasEl, {
        chart: {
          type: "pie",
          backgroundColor: "transparent",
          margin: [0, 0, 0, 0],
          spacing: [0, 0, 0, 0],
        },
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: false },
        tooltip: {
          delayForDisplay: 300,
          useHTML: true,
          outside: true,
          backgroundColor: "white",
          borderColor: "rgba(138,148,153,0.5)",
          borderRadius: 4,
          borderWidth: 1,
          shadow: {
            offsetX: 2,
            offsetY: 4,
            width: 4,
            color: "rgba(0,0,0,0.25)",
          },
          style: { padding: "0" },
          formatter: function () {
            const point = this.point
            const pct = point.percentage || 0
            const count = point.y || 0
            return `<div style="padding: 10px 14px;">
              <div style="font-size: 14px; font-weight: 600; color: ${point.color}; margin-bottom: 4px;">
                ${escapeHtml(point.name)}
              </div>
              <div style="font-size: 13px; color: #374151;">
                ${pct.toFixed(1)}% &middot; ${count.toLocaleString()} ${count === 1 ? t("Location") : t("Locations")}
              </div>
            </div>`
          },
        },
        plotOptions: {
          pie: {
            innerSize: "68%",
            borderWidth: 2,
            borderColor: "#ffffff",
            dataLabels: { enabled: false },
            states: {
              hover: { brightness: 0.05, halo: { size: 4 } },
              inactive: { opacity: 1 },
            },
          },
        },
        series: [
          {
            name: "Locations",
            data: segments.map((s) => ({
              name: s.bucket,
              y: s.location_count || 0,
              color: s.color,
            })),
          },
        ],
      })
    })
  },

  formatPercent(pct) {
    if (pct > 0 && pct < 1) return "<1%"
    return `${Math.round(pct)}%`
  },

  escapeHtml(str) {
    if (str == null) return ""
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  },
}
